/**
 * A general meeting's page (Aziz, 2026-09-27): who, when, the agenda, the outcome of
 * each item, and the follow-up tasks, which are drafts until the organiser has checked
 * every one (person, due date, wording, creative) and sends them.
 */
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { CalendarPlus, Check, Copy, ListChecks, MapPin, Send, Trash2, Users, Video } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import ConfirmDialog from '@/components/ui/ConfirmDialog';
import { cn } from '@/lib/cn';
import {
  addFollowUp, cancelMeeting, dropFollowUp, editFollowUp, fetchMeetingSetup, fileMeetingItems, sendFollowUps, setMeetingItem,
  type FollowUp, type Meeting, type MeetingItem, type Outcome,
} from '@/lib/calls';
import NewMeetingDialog from './NewMeetingDialog';
import RecordingCard from './RecordingCard';
import MinutesCard from './MinutesCard';
import { bidi, BIDI_CLS } from '@/lib/bidi';

const errMsg = (e: unknown) => {
  const d = (e as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  return typeof d === 'string' ? d : 'Something went wrong';
};
const OUTCOMES: Array<{ v: Outcome; label: string; cls: string }> = [
  { v: 'approved', label: 'Approved', cls: 'border-emerald-500 text-emerald-300 bg-emerald-500/15' },
  { v: 'changes', label: 'Changes needed', cls: 'border-amber-500 text-amber-300 bg-amber-500/15' },
  { v: 'discussed', label: 'Discussed', cls: 'border-slate-400 text-slate-200 bg-slate-500/15' },
  { v: 'action', label: 'Action needed', cls: 'border-sky-500 text-sky-300 bg-sky-500/15' },
];
export const GENERAL_STATUS: Record<string, { label: string; cls: string }> = {
  link_sent: { label: 'Waiting for the clinic to pick a time', cls: 'bg-sky-500/15 text-sky-300' },
  booking: { label: 'Booking', cls: 'bg-sky-500/15 text-sky-300' },
  booked: { label: 'Booked', cls: 'bg-emerald-500/15 text-emerald-300' },
  review: { label: 'Record the outcomes', cls: 'bg-amber-500/15 text-amber-300' },
  awaiting_audio: { label: 'Record the outcomes', cls: 'bg-amber-500/15 text-amber-300' },
  filed: { label: 'Filed', cls: 'bg-slate-700/40 text-slate-300' },
  expired: { label: 'Link expired', cls: 'bg-red-500/15 text-red-300' },
  cancelled: { label: 'Cancelled', cls: 'bg-slate-700/40 text-slate-300' },
};

function when(iso?: string | null, tz = 'Asia/Dubai') {
  return iso ? new Date(iso).toLocaleString('en-GB', { timeZone: tz, weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '';
}

export default function MeetingDetail({ m, onChange }: { m: Meeting; onChange: (next?: Meeting) => void }) {
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [followUp, setFollowUp] = useState(false);
  const st = GENERAL_STATUS[m.status] ?? { label: m.status, cls: 'bg-slate-700/40 text-slate-300' };
  const localTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const otherZone = !!m.starts_at && when(m.starts_at) !== when(m.starts_at, localTz);
  const started = !!m.starts_at && new Date(m.starts_at).getTime() <= Date.now() && ['booked', 'review', 'awaiting_audio'].includes(m.status);
  const cancel = useMutation({ mutationFn: () => cancelMeeting(m.id), onSuccess: (x) => { toast.success('Cancelled; everyone is told'); onChange(x); }, onError: (e) => toast.error(errMsg(e)) });
  const creativesOnAgenda = (m.items ?? []).filter((i) => i.creative_id);

  return (
    <div className="max-w-4xl space-y-4" data-testid="general-meeting">
      <div>
        <Link to="/meetings" className="text-xs text-muted hover:text-muted-strong">Back to meetings</Link>
        <div className="flex flex-wrap items-center gap-3 mt-1">
          <h1 className="text-xl font-semibold text-muted-strong">{m.title}</h1>
          <span className={cn('px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wide', st.cls)} data-testid="meeting-status">{st.label}</span>
          {m.dry_run && <span className="px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wide bg-amber-500/15 text-amber-300">dry run</span>}
        </div>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted mt-2">
          {m.starts_at ? (
            <span className="text-muted-strong">{when(m.starts_at)} Dubai time, {m.length_minutes ?? 30} min
              {otherZone && <span className="text-muted"> ({when(m.starts_at, localTz)} your time)</span>}</span>
          ) : <span>{m.length_minutes ?? 30} min, time not picked yet</span>}
          {m.meet_uri && <a href={m.meet_uri} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-accent-400 hover:underline"><Video size={13} /> Join Google Meet</a>}
          {!m.with_meet && m.location && <span className="inline-flex items-center gap-1"><MapPin size={13} /> {m.location}</span>}
        </div>
        {m.last_error && <p className="text-xs text-red-300 mt-1">{m.last_error}</p>}
      </div>

      <Card>
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-wide text-muted mb-2"><Users size={12} /> Who</div>
        <div className="flex flex-wrap gap-1.5 text-xs">
          <span className="px-2 py-1 rounded-full bg-accent-600/20 text-accent-200">{m.organizer_name} (organiser)</span>
          {(m.attendees ?? []).map((a) => <span key={a.id} className="px-2 py-1 rounded-full bg-slate-800 text-muted-strong">{a.name}</span>)}
          {m.invitees.map((i) => <span key={i.email} className="px-2 py-1 rounded-full border border-slate-700 text-muted-strong">{i.name} · {i.kind}</span>)}
        </div>
        {(m.clients ?? []).length > 0 && <div className="text-xs text-muted mt-2">Clinics: {(m.clients ?? []).map((c) => c.name).join(', ')}</div>}
        {m.notes && <p className="text-sm text-muted-strong mt-3 whitespace-pre-wrap">{m.notes}</p>}
        {m.status === 'link_sent' && m.link && (
          <div className="mt-3">
            <Button size="sm" variant="secondary" icon={<Copy size={13} />} onClick={async () => { try { await navigator.clipboard.writeText(m.link!); toast.success('Booking link copied'); } catch { toast.error('Copy did not work'); } }}>Copy the booking link</Button>
          </div>
        )}
      </Card>

      <RecordingCard m={m} showTranscript onChange={onChange} />
      <MinutesCard m={m} editable={started && m.can_manage && m.status !== 'filed'} onChange={onChange} />
      <Agenda m={m} started={started} onChange={onChange} />
      {(m.follow_ups ?? []).length > 0 || m.status === 'filed' ? <FollowUps m={m} creatives={creativesOnAgenda} onChange={onChange} /> : null}

      {m.can_manage && (
        <div className="flex flex-wrap gap-2">
          <Button variant="secondary" icon={<CalendarPlus size={14} />} onClick={() => setFollowUp(true)} data-testid="follow-up-meeting">Follow-up meeting</Button>
          {['link_sent', 'booked'].includes(m.status) && !started && <Button variant="danger" onClick={() => setConfirmCancel(true)} data-testid="cancel-meeting">Cancel the meeting</Button>}
        </div>
      )}
      <ConfirmDialog open={confirmCancel} title="Cancel this meeting?" tone="danger" confirmLabel="Cancel the meeting" cancelLabel="Keep it"
        message="Everyone invited is told, and the time frees up in their calendars." onCancel={() => setConfirmCancel(false)}
        onConfirm={() => { setConfirmCancel(false); cancel.mutate(); }} />
      {followUp && (
        <NewMeetingDialog open onClose={() => setFollowUp(false)} prefill={{
          kind: m.kind === 'client' ? 'client' : 'internal', title: `Follow-up: ${m.title}`.slice(0, 200),
          clientIds: (m.clients ?? []).map((c) => c.id), attendeeIds: (m.attendees ?? []).map((a) => a.id),
          inviteeEmails: m.invitees.map((i) => i.email), followUpOf: m.id,
          items: (m.items ?? []).filter((i) => i.outcome !== 'approved' && i.outcome !== 'discussed').map((i) => ({
            kind: i.kind, id: i.creative_id ?? i.strategy_id ?? (i.kind === 'clinic' ? i.client_id ?? undefined : undefined), label: i.label })),
        }} />
      )}
    </div>
  );
}

function Agenda({ m, started, onChange }: { m: Meeting; started: boolean; onChange: (n?: Meeting) => void }) {
  const [summary, setSummary] = useState(true);
  const editable = started && m.can_manage && m.status !== 'filed';
  const file = useMutation({ mutationFn: () => fileMeetingItems(m.id, summary), onSuccess: (x) => { toast.success('Outcomes filed'); onChange(x); }, onError: (e) => toast.error(errMsg(e)) });
  const items = m.items ?? [];
  const missing = items.filter((i) => !i.outcome).length;
  return (
    <Card data-testid="agenda-card">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-wide text-muted mb-2"><ListChecks size={12} /> Agenda and outcomes</div>
      {items.length === 0 && <p className="text-sm text-muted">No agenda items.</p>}
      <div className="divide-y divide-slate-800">
        {items.map((it) => <AgendaRow key={it.id} m={m} it={it} editable={editable} onChange={onChange} />)}
      </div>
      {!started && m.status !== 'filed' && items.length > 0 && <p className="text-xs text-muted mt-3">Outcomes are recorded here once the meeting has started.</p>}
      {editable && items.length > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-3 mt-4 pt-3 border-t border-slate-800">
          {m.kind === 'client' ? (
            <label className="inline-flex items-center gap-2 text-xs text-muted"><input type="checkbox" checked={summary} onChange={(e) => setSummary(e.target.checked)} />
              Email the clinic a summary (their notes only, in Arabic and English)</label>
          ) : <span />}
          <Button disabled={missing > 0} loading={file.isPending} onClick={() => file.mutate()} data-testid="file-outcomes">
            {missing > 0 ? `${missing} item${missing === 1 ? '' : 's'} without an outcome` : 'File the outcomes'}
          </Button>
        </div>
      )}
    </Card>
  );
}

function AgendaRow({ m, it, editable, onChange }: { m: Meeting; it: MeetingItem; editable: boolean; onChange: (n?: Meeting) => void }) {
  const [clinicNote, setClinicNote] = useState(it.clinic_note ?? '');
  const [staffNote, setStaffNote] = useState(it.staff_note ?? '');
  const save = useMutation({
    mutationFn: (patch: { outcome?: Outcome | null; clinic_note?: string; staff_note?: string }) =>
      setMeetingItem(m.id, it.id, { outcome: it.outcome ?? null, clinic_note: clinicNote, staff_note: staffNote, ...patch }),
    onSuccess: (x) => onChange(x), onError: (e) => toast.error(errMsg(e)),
  });
  const out = OUTCOMES.find((o) => o.v === it.outcome);
  return (
    <div className="py-3" data-testid="agenda-row">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[10px] uppercase text-muted w-16">{it.kind}</span>
        {it.creative_id ? <Link to={`/creatives/${it.creative_id}`} className="text-sm text-muted-strong hover:text-accent-400">{it.label}</Link>
          : <span className="text-sm text-muted-strong">{it.label}</span>}
        {it.script_pending && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300">script waiting for the doctor: Approved here approves it</span>}
        {!editable && out && <span className={cn('text-[11px] px-2 py-0.5 rounded-full border', out.cls)}>{out.label}</span>}
      </div>
      {editable && (
        <div className="mt-2 space-y-2 ps-0 sm:ps-[4.5rem]">
          {!it.outcome && it.suggested_outcome && (
            <div className="flex flex-wrap items-center gap-2 text-xs text-muted" data-testid="suggestion">
              <span>KAF Minutes suggests <span className="text-muted-strong">{OUTCOMES.find((o) => o.v === it.suggested_outcome)?.label}</span>
                {it.suggested_note && <>: <span dir="auto" className={BIDI_CLS}>{bidi(it.suggested_note)}</span></>}</span>
              <button type="button" className="text-accent-400 hover:underline" data-testid="use-suggestion"
                onClick={() => { const note = staffNote || it.suggested_note || ''; setStaffNote(note); save.mutate({ outcome: it.suggested_outcome as Outcome, staff_note: note }); }}>Use it</button>
            </div>
          )}
          <div className="flex flex-wrap gap-1.5" role="radiogroup" aria-label={`Outcome for ${it.label}`}>
            {OUTCOMES.map((o) => (
              <button key={o.v} type="button" role="radio" aria-checked={it.outcome === o.v} onClick={() => save.mutate({ outcome: o.v })} data-testid={`outcome-${o.v}`}
                className={cn('px-2.5 py-1 rounded-full border text-xs min-h-[32px]', it.outcome === o.v ? o.cls : 'border-slate-700 text-muted hover:text-muted-strong')}>
                {it.outcome === o.v && <Check size={11} className="inline mr-1" />}{o.label}
              </button>
            ))}
          </div>
          <div className="grid gap-2 sm:grid-cols-2">
            {m.kind === 'client' && (
              <textarea value={clinicNote} onChange={(e) => setClinicNote(e.target.value)} onBlur={() => clinicNote !== (it.clinic_note ?? '') && save.mutate({})}
                rows={2} maxLength={1000} placeholder="Note for the clinic (goes in their summary)" className="px-3 py-2 rounded-lg bg-bg border border-slate-800 text-sm" />
            )}
            <textarea value={staffNote} onChange={(e) => setStaffNote(e.target.value)} onBlur={() => staffNote !== (it.staff_note ?? '') && save.mutate({})}
              rows={2} maxLength={2000} placeholder="Note for the team (stays inside KAF)" className="px-3 py-2 rounded-lg bg-bg border border-slate-800 text-sm" />
          </div>
        </div>
      )}
      {!editable && (it.clinic_note || it.staff_note) && (
        <div className="mt-1 ps-0 sm:ps-[4.5rem] text-xs text-muted space-y-0.5">
          {it.clinic_note && <div>To the clinic: {it.clinic_note}</div>}
          {it.staff_note && <div>Team: {it.staff_note}</div>}
        </div>
      )}
    </div>
  );
}

function FollowUps({ m, creatives, onChange }: { m: Meeting; creatives: MeetingItem[]; onChange: (n?: Meeting) => void }) {
  const qc = useQueryClient();
  // the whole team, readable by anyone on staff (the admin user list is not)
  const setup = useQuery({ queryKey: ['meeting-setup', (m.clients ?? []).map((c) => c.id).join(','), ''], queryFn: () => fetchMeetingSetup((m.clients ?? []).map((c) => c.id)) });
  // a task belongs to a creative: search this meeting's clinics, or all of yours for a team meeting
  const [find, setFind] = useState('');
  const [findQ, setFindQ] = useState('');
  const searchIn = (m.clients ?? []).length ? (m.clients ?? []).map((c) => c.id) : (setup.data?.clinics ?? []).map((c) => c.id);
  const found = useQuery({ queryKey: ['meeting-setup', searchIn.join(','), findQ], queryFn: () => fetchMeetingSetup(searchIn, findQ), enabled: searchIn.length > 0 });
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const drafts = (m.follow_ups ?? []).filter((f) => f.status === 'draft');
  const sent = (m.follow_ups ?? []).filter((f) => f.status === 'sent' || f.status === 'done');
  const team = (setup.data?.team ?? []).map((u) => ({ id: u.id, display_name: u.name }));
  const creativeOptions = useMemo(() => {
    const seen = new Map<string, string>();
    for (const c of creatives) if (c.creative_id) seen.set(c.creative_id, c.label);
    for (const a of [...(setup.data?.agenda ?? []), ...(found.data?.agenda ?? [])]) if (a.kind === 'creative' && !seen.has(a.id)) seen.set(a.id, a.label);
    return [...seen.entries()];
  }, [creatives, setup.data, found.data]);
  const add = useMutation({ mutationFn: () => addFollowUp(m.id, { title: 'New task' }), onSuccess: onChange, onError: (e) => toast.error(errMsg(e)) });
  const send = useMutation({
    mutationFn: () => sendFollowUps(m.id, [...picked]),
    onSuccess: (x) => { toast.success(`${picked.size} task${picked.size === 1 ? '' : 's'} sent`); setPicked(new Set()); qc.invalidateQueries({ queryKey: ['tasks'] }); onChange(x); },
    onError: (e) => toast.error(errMsg(e)),
  });
  if (!m.can_manage && drafts.length === 0 && sent.length === 0) return null;

  return (
    <Card data-testid="follow-ups">
      <div className="flex items-center justify-between gap-2 mb-1">
        <div className="text-[10px] uppercase tracking-wide text-muted">Follow-up tasks</div>
        {m.can_manage && <Button size="sm" variant="ghost" onClick={() => add.mutate()}>Add a task</Button>}
      </div>
      {drafts.length > 0 && <p className="text-xs text-muted mb-3">Drafts from the outcomes and from KAF Minutes. Check each one: the person, the due date and the words. Link a creative when the action is about one; without a creative it goes to the person's My tasks as a meeting action. Nothing is sent until you press Send.</p>}
      {drafts.length > 0 && m.can_manage && (
        <input value={find} onChange={(e) => setFind(e.target.value)} onKeyUp={() => { const v = find; setTimeout(() => setFindQ(v), 200); }}
          placeholder="Find a creative for a task (code or title)" className="mb-3 w-full sm:w-80 h-8 px-2 rounded-lg bg-bg border border-slate-800 text-xs" data-testid="find-creative" />
      )}
      <div className="space-y-3">
        {drafts.map((f) => (
          <DraftRow key={f.id} f={f} canEdit={m.can_manage} team={team} creativeOptions={creativeOptions} picked={picked.has(f.id)}
            onPick={(v) => setPicked((s) => { const n = new Set(s); if (v) n.add(f.id); else n.delete(f.id); return n; })} onChange={onChange} />
        ))}
      </div>
      {drafts.length > 0 && m.can_manage && (
        <div className="flex justify-end mt-3">
          <Button icon={<Send size={13} />} disabled={picked.size === 0} loading={send.isPending} onClick={() => send.mutate()} data-testid="send-follow-ups">
            {picked.size ? `Send ${picked.size} reviewed task${picked.size === 1 ? '' : 's'}` : 'Tick the tasks you have checked'}
          </Button>
        </div>
      )}
      {sent.length > 0 && (
        <ul className="mt-3 space-y-1 text-sm">
          {sent.map((f) => <li key={f.id} className="flex flex-wrap items-center gap-2 text-muted-strong"><Check size={13} className={f.status === 'done' || f.task_id ? 'text-emerald-400' : 'text-muted'} />{f.title}
            <span className="text-xs text-muted">· {f.assignee_name}{f.due_date ? `, due ${new Date(f.due_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}` : ''}
              {!f.task_id && (f.status === 'done' ? ', done' : ', open')}</span></li>)}
        </ul>
      )}
    </Card>
  );
}

function DraftRow({ f, canEdit, team, creativeOptions, picked, onPick, onChange }: {
  f: FollowUp; canEdit: boolean; team: Array<{ id: string; display_name: string }>; creativeOptions: Array<[string, string]>;
  picked: boolean; onPick: (v: boolean) => void; onChange: (n?: Meeting) => void;
}) {
  const [title, setTitle] = useState(f.title);
  const [notes, setNotes] = useState(f.notes ?? '');
  const save = useMutation({ mutationFn: (body: Partial<FollowUp>) => editFollowUp(f.id, body), onSuccess: onChange, onError: (e) => toast.error(errMsg(e)) });
  const drop = useMutation({ mutationFn: () => dropFollowUp(f.id), onSuccess: onChange, onError: (e) => toast.error(errMsg(e)) });
  const complete = !!(f.assignee_id && f.due_date && title.trim());
  return (
    <div className={cn('rounded-lg border p-3 space-y-2', picked ? 'border-accent-400/70' : 'border-slate-800')} data-testid="draft-row">
      <div className="flex items-start gap-2">
        <input type="checkbox" className="accent-accent-400 mt-2.5" checked={picked} disabled={!canEdit || !complete} onChange={(e) => onPick(e.target.checked)}
          aria-label="Checked and ready to send" title={complete ? 'Checked and ready to send' : 'Needs a person and a due date'} />
        <input value={title} disabled={!canEdit} onChange={(e) => setTitle(e.target.value)} onBlur={() => title.trim() && title !== f.title && save.mutate({ title })}
          className="flex-1 min-w-0 h-9 px-2 rounded-lg bg-bg border border-slate-800 text-sm text-muted-strong" />
        {canEdit && <button type="button" onClick={() => drop.mutate()} className="p-2 text-muted hover:text-red-300" aria-label="Drop this task"><Trash2 size={14} /></button>}
      </div>
      <div className="grid gap-2 sm:grid-cols-3 ps-6">
        <select value={f.assignee_id ?? ''} disabled={!canEdit} onChange={(e) => save.mutate({ assignee_id: e.target.value })} aria-label="Who does it"
          className="h-9 px-2 rounded-lg bg-bg border border-slate-800 text-sm">
          <option value="">Who does it?</option>
          {team.map((u) => <option key={u.id} value={u.id}>{u.display_name}</option>)}
        </select>
        <select value={f.creative_id ?? ''} disabled={!canEdit} onChange={(e) => save.mutate({ creative_id: e.target.value })} aria-label="Which creative"
          className="h-9 px-2 rounded-lg bg-bg border border-slate-800 text-sm">
          <option value="">No creative (a meeting action)</option>
          {creativeOptions.map(([id, label]) => <option key={id} value={id}>{label}</option>)}
        </select>
        <input type="date" value={f.due_date ?? ''} disabled={!canEdit} onChange={(e) => save.mutate({ due_date: e.target.value })} aria-label="Due date"
          className="h-9 px-2 rounded-lg bg-bg border border-slate-800 text-sm" />
      </div>
      <textarea value={notes} disabled={!canEdit} onChange={(e) => setNotes(e.target.value)} onBlur={() => notes !== (f.notes ?? '') && save.mutate({ notes })}
        rows={2} placeholder="Details for the person doing it" className="ms-6 w-[calc(100%-1.5rem)] px-2 py-1.5 rounded-lg bg-bg border border-slate-800 text-sm" />
      {f.source === 'kaf_minutes' && <div className="ps-6 text-[11px] text-muted">Suggested by KAF Minutes{f.when_said ? <>. Said about timing: “<span dir="auto">{f.when_said}</span>”</> : ''}</div>}
      {!complete && <div className="ps-6 text-[11px] text-amber-300">Needs {[!f.assignee_id && 'a person', !f.due_date && 'a due date'].filter(Boolean).join(' and ')} before it can be sent.</div>}
    </div>
  );
}
